import type {Theme} from "@src/features/settings/settings-models";
import {themeStorage} from "@src/features/settings/settings-storage";
import {setThemeOnDocument} from "@src/features/settings/utils/theme-utils";
import * as React from "react";


type ThemeProviderProps = {
	children: React.ReactNode
	defaultTheme: Theme
}


type ThemeProviderState = {
	theme: Theme
	setTheme: (theme: Theme) => void
}


const initialState: ThemeProviderState = {
	theme: "system",
	setTheme: () => null,
};


const ThemeProviderContext = React.createContext<ThemeProviderState>(initialState);


export function ThemeProvider({
	                              children,
	                              defaultTheme,
	                              ...props
                              }: ThemeProviderProps) {
	const [theme, setTheme] = React.useState<Theme>(
		() => themeStorage.loadTheme(defaultTheme)
	);
	
	React.useEffect(() => {
		setThemeOnDocument(theme);
	}, [theme]);
	
	const themeProviderState = {
		theme: theme,
		setTheme: (theme: Theme) => {
			themeStorage.saveTheme(theme);
			setTheme(theme);
		},
	} satisfies ThemeProviderState as ThemeProviderState;
	
	return (
		<ThemeProviderContext.Provider {...props} value={themeProviderState}>
			{children}
		</ThemeProviderContext.Provider>
	);
}


export function useTheme(): ThemeProviderState {
	const context = React.useContext(ThemeProviderContext);
	
	if (context === undefined)
		throw new Error("useTheme must be used within a ThemeProvider");
	
	return context;
}
